import { Feature } from '~shared/types/pricing/pricingsvc';
import { PackageDataSet } from '~shared/types/pricing/package';

import { formatDetailsFeature } from '~pricing/lib/PackageFeatures';
import { convertFeatureSetToMap } from '~pricing/lib/lib-helpers';
import { FeatureSetMap, FeatureMapItem } from '~pricing/transformers/types';

import detailsFeatureRules from '~pricing/transformers/details/rules';

import COPY from '~pricing/lib/PackageFeatures/details/copy';

import {
  DetailsFeatureCategoryGroups,
  DetailsFeatureSets,
  DetailsFeatureValueType,
  DetailsFeatureRow,
  DetailsFeatureRowFeature,
  FeaturesCategoryGroup,
  DetailsFeatureRowPackages,
  DetailsFeatureRulesMap,
} from './types';

import { getFormattedAmountValue } from './helpers';
import experimentFeatures from './experimentFeatures';
import fakeFeatures from './marketingFeatures';
import combinedFeatures from './combinedFeatures';

/**
   * Returns an array with the following structure
   * [
    {
      name: 'Team Collaboration',
      features: [
        {
          feature: 'team_themes',
          title: 'Shared themes',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: true,
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: true,
            },
            {
              packageName: 'ENT_PLATINUM',
              value: true,
            },
          ],
        },
        {
          feature: 'group_templates',
          title: 'Shared templates',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: false,
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: true,
            },
            {
              packageName: 'ENT_PLATINUM',
              value: true,
            },
          ],
        },
      ],
    },
    {
      name: 'Survey Capabilities',
      features: [
        {
          feature: 'survey_create_limit',
          title: 'Number of surveys',
          packages: [
            {
              packageName: 'TEAM_ADVANTAGE_ANNUAL',
              value: 'UNLIMITED',
            },
            {
              packageName: 'TEAM_PREMIER_ANNUAL',
              value: 'UNLIMITED',
            },
            {
              packageName: 'ENT_PLATINUM',
              value: 'UNLIMITED',
            },
          ],
        },
      ],
    },
  ];
  */

/**
 * Determines the value to display for that feature based on its featureRule given the following preconditions
 * @param packageName
 * @param feature
 * @param locale
 * @returns
 */
function getFeatureValue(
  packageName: string,
  feature: Feature,
  locale: string,
  experience: string,
  canonicalPath: string
): DetailsFeatureValueType {
  const featureRulesObj = canonicalPath
    ? detailsFeatureRules[canonicalPath as keyof DetailsFeatureRulesMap]
    : detailsFeatureRules.teams;
  const rules = experience && featureRulesObj[experience] ? featureRulesObj[experience] : featureRulesObj.default;

  if (feature?.amount) {
    return rules[feature?.name]
      ? rules[feature.name](packageName, feature, { locale })
      : getFormattedAmountValue(feature, { locale });
  }

  if (feature?.setting) {
    return rules[feature?.name] ? rules[feature.name](packageName, feature) : feature?.setting;
  }

  return rules[feature?.name] ? rules[feature.name](packageName) : feature?.isEnabled;
}

/**
 * Determines if the current feature being looped over is part of a combined list,
 * meaning only show one line item for a set of given features, otherwise just show the feature as its own line item
 * @param packageName
 * @param packageFeatures
 * @param feature
 * @param locale
 * @returns
 */
function determineCombinedFeature(
  packageName: string,
  packageFeatures: FeatureSetMap,
  feature: string,
  locale: string,
  experience: string,
  canonicalPath: string
): DetailsFeatureValueType {
  const combinedFeature: FeatureMapItem =
    combinedFeatures.find(combined => combined.features.includes(feature)) ?? null;
  if (combinedFeature) {
    const features = combinedFeature?.features?.reduce<FeatureSetMap>((acc, key) => {
      acc[key] = packageFeatures[key];
      return acc;
    }, {});

    const featureRulesObj = detailsFeatureRules[canonicalPath as keyof DetailsFeatureRulesMap];
    const rules = experience && featureRulesObj[experience] ? featureRulesObj[experience] : featureRulesObj.default;

    return features ? rules[combinedFeature.featureItemKey](packageName, features, { locale }) : null;
  }
  return null;
}

/**
 * Transforms detail pages features
 * This is the main function that loops over all category groupings and which features they contain
 * @param displayedPackages
 * @param groupings
 * @param locale
 * @returns
 */
export default function getFeatureCategories(
  displayedPackages: PackageDataSet,
  groupings: DetailsFeatureCategoryGroups,
  locale: string,
  overageCost: string,
  experience: string,
  canonicalPath: string
): DetailsFeatureSets {
  const transformedPackages = displayedPackages.map(pkg => {
    return {
      ...pkg,
      features: convertFeatureSetToMap([...pkg?.features, ...fakeFeatures, ...experimentFeatures]),
    };
  });

  return groupings.map(({ category, features }: FeaturesCategoryGroup): DetailsFeatureRow => {
    return {
      name: category,
      features: features?.map((feature: string): DetailsFeatureRowFeature => {
        return {
          ...formatDetailsFeature(feature as keyof typeof COPY, { overageCost }),
          packages: transformedPackages.map(pkg => {
            return {
              packageName: pkg.packageName,
              value:
                determineCombinedFeature(pkg.packageName, pkg.features, feature, locale, experience, canonicalPath) ??
                getFeatureValue(pkg.packageName, pkg.features[feature] || feature, locale, experience, canonicalPath),
            };
          }) as DetailsFeatureRowPackages[],
        };
      }),
    };
  });
}
